import { useState } from "react";
import { ListChecks, Pencil, Save, X } from "lucide-react";
import { Button } from "../../../../ui/button";
import { Textarea } from "../../../../ui/textarea";
import { updateJob } from "../../../../../apiManager/houseOwner/updateJob";
import { toast } from "sonner";
import { useSelector } from "react-redux";

export function ServiceInstructions({ jobData, onUpdate }) {
    const [isEditing, setIsEditing] = useState(false);
    const [instructionsText, setInstructionsText] = useState(
        jobData.serviceDetail.serviceInstruction.join("\n")
    );
    const [loading, setLoading] = useState(false);

    const userInfo = useSelector(state => state.auth.userInfo);
    const isHouseOwner = userInfo?.user?.role === 'houseOwner';

    const canEdit = isHouseOwner && (jobData.jobStatus === 'open' || jobData.jobStatus === 'in-progress');

    const handleSave = async () => {
        try {
            setLoading(true);
            const newInstructions = instructionsText
                .split("\n")
                .map(line => line.trim())
                .filter(line => line.length > 0);

            const updatePayload = {
                serviceDetail: {
                    ...jobData.serviceDetail,
                    serviceInstruction: newInstructions
                }
            };

            const res = await updateJob(jobData._id, updatePayload);

            if (res.error) {
                throw new Error(res.error);
            }

            toast.success("Instructions updated successfully");

            setIsEditing(false);
            if (onUpdate) onUpdate(); // Trigger reload of job details

        } catch (error) {
            console.error("Failed to update instructions:", error);
            toast.error("Failed to update instructions");
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        setInstructionsText(jobData.serviceDetail.serviceInstruction.join("\n"));
        setIsEditing(false);
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <ListChecks className="w-4 h-4 text-green-600" />
                    <h3 className="font-semibold text-gray-900">Instructions</h3>
                </div>
                {canEdit && !isEditing && (
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setIsEditing(true)}
                        className="h-8 px-2 text-gray-500 hover:text-blue-600"
                    >
                        <Pencil className="w-4 h-4 mr-1" />
                        Edit
                    </Button>
                )}
            </div>

            {isEditing ? (
                <div className="space-y-3">
                    <Textarea
                        value={instructionsText}
                        onChange={(e) => setInstructionsText(e.target.value)}
                        placeholder="Enter instructions (one per line)"
                        className="min-h-[150px]"
                    />
                    <div className="flex gap-2 justify-end">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleCancel}
                            disabled={loading}
                        >
                            <X className="w-4 h-4 mr-1" />
                            Cancel
                        </Button>
                        <Button
                            size="sm"
                            onClick={handleSave}
                            disabled={loading}
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                        >
                            <Save className="w-4 h-4 mr-1" />
                            {loading ? "Saving..." : "Save"}
                        </Button>
                    </div>
                </div>
            ) : (
                <div className="space-y-2">
                    {jobData.serviceDetail.serviceInstruction && jobData.serviceDetail.serviceInstruction.length > 0 ? (
                        jobData.serviceDetail.serviceInstruction.map((instruction, index) => (
                            <div
                                key={index}
                                className="flex items-start gap-3 p-3 bg-green-50 rounded-lg border border-green-100"
                            >
                                <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0" />
                                <p className="text-gray-700 text-sm">{instruction}</p>
                            </div>
                        ))
                    ) : (
                        <p className="text-gray-500 text-sm text-center py-2">
                            No specific instructions provided
                        </p>
                    )}
                </div>
            )}
        </div>
    );
}
